import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import ApiService from '../services/apiService';
import '../styles/AudioPlayer.css';
import { logger } from '../utils/logger';

interface AudioPlayerProps {
    audioUrl?: string;
    fileId: string;
    onAudioLoaded?: (duration: number) => void;
    onTimeUpdate?: (currentTime: number) => void;
    compact?: boolean;
}

export interface AudioPlayerRef {
    seekTo: (time: number) => void;
    play: () => void;
    pause: () => void;
    getCurrentTime: () => number;
    getDuration: () => number;
}

const AudioPlayer = forwardRef<AudioPlayerRef, AudioPlayerProps>(({
    audioUrl,
    fileId,
    onAudioLoaded,
    onTimeUpdate,
    compact = false
}, ref) => {
    const { t } = useTranslation();
    const [isPlaying, setIsPlaying] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [audioSrc, setAudioSrc] = useState<string | null>(null);
    const [hasStartedPlayback, setHasStartedPlayback] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [hasControlFocus, setHasControlFocus] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Fouttekst pas tonen nadat de gebruiker zelf op afspelen heeft gedrukt;
    // tot die tijd is alleen het waarschuwingsicoon op de knop zichtbaar.
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [shouldPlayAfterLoad, setShouldPlayAfterLoad] = useState(false);

    const audioRef = useRef<HTMLAudioElement>(null);
    const compactPlayerRef = useRef<HTMLDivElement>(null);
    const compactButtonRef = useRef<HTMLButtonElement>(null);
    const pointerDownRef = useRef(false);
    const sourceModeRef = useRef<'stream' | 'blob' | null>(null);
    const refreshAttemptsRef = useRef(0);
    const pendingResumeRef = useRef<{ time: number; play: boolean } | null>(null);

    const showCompactControls = hasStartedPlayback && (isHovered || hasControlFocus);

    useEffect(() => {
        if (!compact || !showCompactControls) return;
        const dismissOutside = (event: PointerEvent) => {
            if (event.target instanceof Node && !compactPlayerRef.current?.contains(event.target)) {
                setIsHovered(false);
                setHasControlFocus(false);
            }
        };
        document.addEventListener('pointerdown', dismissOutside);
        return () => document.removeEventListener('pointerdown', dismissOutside);
    }, [compact, showCompactControls]);

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
        seekTo: (time: number) => {
            if (audioRef.current) {
                audioRef.current.currentTime = time;
                setCurrentTime(time);
            }
        },
        play: async () => {
            if (audioRef.current && audioSrc) {
                try {
                    await audioRef.current.play();
                    setIsPlaying(true);
                    setHasStartedPlayback(true);
                } catch (error) {
                    logger.error('Error playing audio:', error);
                    setError(t('audioPlayer.errors.playFailed'));
                }
            }
        },
        pause: () => {
            if (audioRef.current) {
                audioRef.current.pause();
                setIsPlaying(false);
            }
        },
        getCurrentTime: () => currentTime,
        getDuration: () => duration,
    }), [audioSrc, currentTime, duration, t]);

    useEffect(() => {
        // Load audio via API service when component mounts
        if (audioUrl && !audioSrc && !isLoading) {
            logger.log('Audio URL provided:', audioUrl);
            logger.log('Loading audio via API service...');
            loadAudio();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- alleen opnieuw laden bij een andere audioUrl
    }, [audioUrl]);

    // Auto-play after audio is loaded if requested
    useEffect(() => {
        if (audioSrc && shouldPlayAfterLoad && audioRef.current) {
            logger.log('Auto-playing audio after load, audioSrc:', audioSrc);
            const tryPlay = async () => {
                try {
                    logger.log('Attempting to play audio...');

                    // Wait for the audio to be ready
                    if (audioRef.current!.readyState < 2) {
                        logger.log('Audio not ready, waiting for loadeddata event...');
                        await new Promise((resolve) => {
                            const onLoadedData = () => {
                                audioRef.current!.removeEventListener('loadeddata', onLoadedData);
                                resolve(void 0);
                            };
                            audioRef.current!.addEventListener('loadeddata', onLoadedData);
                        });
                    }

                    await audioRef.current!.play();
                    logger.log('Audio started playing successfully');
                    setIsPlaying(true);
                    setHasStartedPlayback(true);
                    setShouldPlayAfterLoad(false);
                } catch (error) {
                    logger.error('Error auto-playing audio after load:', error);
                    setError(t('audioPlayer.errors.playFailed'));
                    setShouldPlayAfterLoad(false);
                }
            };
            tryPlay();
        }
    }, [audioSrc, shouldPlayAfterLoad, t]);

    // Blob-URL's (fallback-pad) opruimen bij wisselen van bron of unmount
    useEffect(() => {
        if (!audioSrc || !audioSrc.startsWith('blob:')) return;
        const url = audioSrc;
        return () => URL.revokeObjectURL(url);
    }, [audioSrc]);

    const loadAudio = async () => {
        if (audioSrc || isLoading) return;

        setIsLoading(true);
        setError(null);
        try {
            const apiService = new ApiService();
            let src: string;
            try {
                // Stream-endpoint: browser streamt zelf progressief via Range-requests
                const stream = await apiService.getAudioStreamUrl(fileId);
                sourceModeRef.current = 'stream';
                if (stream.duration && stream.duration > 0) {
                    setDuration(stream.duration);
                    onAudioLoaded?.(stream.duration);
                }
                src = stream.url;
            } catch (streamError) {
                const status = (streamError as any)?.response?.status;
                if (status === 410) {
                    throw new Error(t('audioPlayer.errors.retention'));
                }
                if (status === 409) {
                    throw new Error(t('audioPlayer.errors.uploadBusy'));
                }
                // Oudere server zonder stream-endpoint → volledige download als blob
                const audioBlob = await apiService.getAudio(fileId);
                if (audioBlob.size === 0) {
                    throw new Error(t('audioPlayer.errors.emptyFile'));
                }
                sourceModeRef.current = 'blob';
                src = URL.createObjectURL(audioBlob);
            }
            setAudioSrc(src);
        } catch (error) {
            const message = error instanceof Error && !(error as any).isAxiosError
                ? error.message
                : t('audioPlayer.errors.loadFailed');
            setError(message);
        } finally {
            setIsLoading(false);
        }
    };

    // Stream-token verlopen (401 op een Range-request) of bron weggevallen:
    // verse stream-URL aanvragen en afspeelpositie herstellen.
    const handleAudioError = async () => {
        if (sourceModeRef.current !== 'stream' || !audioRef.current) {
            setError(t('audioPlayer.errors.playFailed'));
            setIsPlaying(false);
            return;
        }
        if (refreshAttemptsRef.current >= 2) {
            setError(t('audioPlayer.errors.loadFailed'));
            setIsPlaying(false);
            return;
        }
        refreshAttemptsRef.current += 1;
        pendingResumeRef.current = {
            time: audioRef.current.currentTime || currentTime,
            play: isPlaying
        };
        try {
            const stream = await new ApiService().getAudioStreamUrl(fileId);
            setAudioSrc(stream.url);
        } catch (refreshError) {
            const status = (refreshError as any)?.response?.status;
            pendingResumeRef.current = null;
            setIsPlaying(false);
            setError(status === 410
                ? t('audioPlayer.errors.retention')
                : t('audioPlayer.errors.loadFailed'));
        }
    };

    const handlePlayPause = async () => {
        setShowErrorMessage(true);

        if (error && !audioSrc) {
            // Bron kon niet geladen worden (bv. upload nog bezig): opnieuw proberen
            await loadAudio();
            return;
        }

        if (!audioRef.current) return;

        if (!audioSrc) {
            // console.log('No audio source available, setting flag to play after load');
            setShouldPlayAfterLoad(true);
            return;
        }

        if (isPlaying) {
            // console.log('Pausing audio');
            audioRef.current.pause();
            setIsPlaying(false);
        } else {
            try {
                // console.log('Playing audio, current src:', audioRef.current.src);
                await audioRef.current.play();
                // console.log('Audio started playing');
                setIsPlaying(true);
                setHasStartedPlayback(true);
            } catch (error) {
                logger.error('Error playing audio:', error);
                setError(t('audioPlayer.errors.playFailed'));
            }
        }
    };

    const handleTimeUpdate = () => {
        if (audioRef.current) {
            const time = audioRef.current.currentTime;
            refreshAttemptsRef.current = 0;
            setCurrentTime(time);
            onTimeUpdate?.(time);
        }
    };

    const handleLoadedMetadata = () => {
        if (!audioRef.current) return;

        const dur = audioRef.current.duration;
        if (isFinite(dur) && dur > 0) {
            setDuration(dur);
            onAudioLoaded?.(dur);
        }

        const resume = pendingResumeRef.current;
        if (resume) {
            pendingResumeRef.current = null;
            audioRef.current.currentTime = resume.time;
            setCurrentTime(resume.time);
            if (resume.play) {
                audioRef.current.play()
                    .then(() => setIsPlaying(true))
                    .catch(() => setIsPlaying(false));
            }
        }
    };

    const handleSeek = (value: number) => {
        if (audioRef.current) {
            audioRef.current.currentTime = value;
            setCurrentTime(value);
        }
    };

    const formatTime = (seconds: number): string => {
        if (isNaN(seconds)) return '00:00';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;

    if (compact) {
        const playPauseIcon = isLoading ? (
            <span className="compact-audio-player__spinner" />
        ) : error ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                <path d="M12 9v4m0 4h.01" />
            </svg>
        ) : isPlaying ? (
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" /></svg>
        ) : (
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>
        );
        const playPauseLabel = error ?? (isPlaying ? t('audioPlayer.pause') : t('audioPlayer.play'));

        return (
            <div
                ref={compactPlayerRef}
                className={`compact-audio-player${isPlaying ? ' is-playing' : ''}`}
                onPointerEnter={event => { if (event.pointerType !== 'touch') setIsHovered(true); }}
                onPointerLeave={event => {
                    pointerDownRef.current = false;
                    if (event.pointerType !== 'touch') setIsHovered(false);
                }}
                onPointerDown={event => {
                    pointerDownRef.current = true;
                    setHasControlFocus(event.pointerType === 'touch');
                }}
                onPointerUp={() => { pointerDownRef.current = false; }}
                onPointerCancel={() => { pointerDownRef.current = false; }}
                onFocus={event => {
                    if (!pointerDownRef.current && event.target.matches(':focus-visible')) setHasControlFocus(true);
                }}
                onBlur={event => {
                    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setHasControlFocus(false);
                }}
                onKeyDown={event => {
                    if (event.key === 'Escape' && showCompactControls) {
                        event.preventDefault();
                        event.stopPropagation();
                        compactButtonRef.current?.focus({ preventScroll: true });
                        setIsHovered(false);
                        setHasControlFocus(false);
                    }
                }}
            >
                {audioSrc && (
                    <audio
                        ref={audioRef}
                        src={audioSrc}
                        preload="metadata"
                        onTimeUpdate={handleTimeUpdate}
                        onLoadedMetadata={handleLoadedMetadata}
                        onError={handleAudioError}
                        onEnded={() => {
                            if (compactPlayerRef.current?.contains(document.activeElement)) {
                                compactButtonRef.current?.focus({ preventScroll: true });
                            }
                            setIsPlaying(false);
                            setHasStartedPlayback(false);
                        }}
                    />
                )}

                <button
                    ref={compactButtonRef}
                    type="button"
                    onClick={handlePlayPause}
                    disabled={isLoading}
                    className={`compact-audio-player__button${error ? ' is-warning' : ''}`}
                    title={playPauseLabel}
                    aria-label={playPauseLabel}
                >
                    {playPauseIcon}
                </button>

                <div className="compact-audio-player__overlay" hidden={!showCompactControls}>
                    <div className="compact-audio-player__controls">
                        <button
                            type="button"
                            onClick={handlePlayPause}
                            disabled={isLoading}
                            className={`compact-audio-player__button${error ? ' is-warning' : ''}`}
                            aria-label={playPauseLabel}
                            title={playPauseLabel}
                        >
                            {playPauseIcon}
                        </button>
                        <div className="compact-audio-player__timeline">
                            <input
                                type="range"
                                min="0"
                                max={duration || 0}
                                step="0.1"
                                value={currentTime}
                                aria-label={t('audioPlayer.playbackPosition')}
                                aria-valuetext={`${formatTime(currentTime)} / ${formatTime(duration)}`}
                                onChange={event => handleSeek(Number(event.target.value))}
                                disabled={!audioSrc || isLoading || duration <= 0}
                                className="compact-audio-player__seek"
                                style={{ '--audio-progress': `${progressPercentage}%` } as React.CSSProperties}
                            />
                            <div className="compact-audio-player__time" aria-hidden="true">
                                <span>{formatTime(currentTime)}</span>
                                <span>{formatTime(duration)}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Full version (non-compact) — detail-player rij volgens de oats-spec
    return (
        <div className="detail-player">
            {audioSrc && (
                <audio
                    ref={audioRef}
                    src={audioSrc}
                    preload="metadata"
                    onTimeUpdate={handleTimeUpdate}
                    onLoadedMetadata={handleLoadedMetadata}
                    onError={handleAudioError}
                    onEnded={() => setIsPlaying(false)}
                />
            )}

            <button
                type="button"
                className={`detail-player-btn${error ? ' is-warning' : ''}`}
                onClick={handlePlayPause}
                disabled={isLoading}
                title={error ?? undefined}
                aria-label={error ?? (isPlaying ? t('audioPlayer.pause') : t('audioPlayer.play'))}
            >
                {isLoading ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                ) : error ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                        <line x1="12" y1="9" x2="12" y2="13" />
                        <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                ) : isPlaying ? (
                    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" /></svg>
                ) : (
                    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                )}
            </button>

            <div className="detail-player-progress">
                <span className="time-label">{formatTime(currentTime)}</span>
                <input
                    type="range"
                    className="progress-bar"
                    aria-label={t('audioPlayer.playbackPosition')}
                    min="0"
                    max={duration || 0}
                    value={currentTime}
                    onChange={(e) => handleSeek(Number(e.target.value))}
                    disabled={!audioSrc || isLoading}
                    style={{
                        background: `linear-gradient(to right, var(--primary-700) 0%, var(--primary-700) ${progressPercentage}%, var(--detail-track, var(--primary-200)) ${progressPercentage}%, var(--detail-track, var(--primary-200)) 100%)`
                    }}
                />
                <span className="time-label">{formatTime(duration)}</span>
                {error && showErrorMessage && (
                    <div className="detail-player-error" role="alert">{error}</div>
                )}
            </div>
        </div>
    );
});

export default AudioPlayer; 